import { createSelector } from 'reselect';
import _get from 'lodash/get';
import { getLatestJob } from 'lib/selectors/getLatestJob';
import { formatOrderOpName } from 'lib/utils/jobDisplayName';
import { getCurrentWorkOrderOp } from './getCurrentWorkOrderOp';
import { getLatestActivitySet } from './getLatestActivitySet';
import { getPostLaborUpdatesOnNats } from './getPostLaborUpdatesOnNats';
import { getIsPausedSetup } from './getIsPausedSetup';
import { getIsApmERPJobFlow } from './getIsApmERPJobFlow';
import { getIsSetupModeActive } from './getIsSetupModeActive';
import { getJobDisplayNameFormat } from './getJobDisplayNameFormat';

/**
 * Resolves the job name to display for the current operation.
 * In NATS (post-labor) flow uses the current work order op; in standard flow
 * uses the work order op when it matches the current activity set, otherwise
 * the job's display name (avoids stale name while WO op loads after a switch).
 *
 * @returns {string} Display name for the latest job / current operation
 */
const getLatestJobName = createSelector(
  [
    getLatestJob,
    getCurrentWorkOrderOp,
    getLatestActivitySet,
    getPostLaborUpdatesOnNats,
    getIsPausedSetup,
    getIsApmERPJobFlow,
    getIsSetupModeActive,
    getJobDisplayNameFormat,
  ],
  (
    latestJob,
    currentWorkOrderOp,
    latestActivitySet,
    natsFlowEnabled,
    isPausedSetup,
    isApmERPJobFlow,
    isSetupModeActive,
    jobDisplayNameFormat
  ) => {
    if (isApmERPJobFlow && isSetupModeActive && !currentWorkOrderOp) {
      return 'No active setups';
    }

    // APM ERP flow: use work order op display name
    if (isApmERPJobFlow && currentWorkOrderOp) {
      return formatOrderOpName(currentWorkOrderOp, jobDisplayNameFormat);
    }

    // NATS flow: build name from work order op (skip when paused in setup)
    if (natsFlowEnabled && !isPausedSetup && currentWorkOrderOp) {
      return formatOrderOpName(currentWorkOrderOp, jobDisplayNameFormat);
    }

    // Only use currentWorkOrderOp when it matches the current activity set,
    // so we don't show a stale name while the WO op query loads after a switch.
    const workOrderOpMatchesActivitySet =
      currentWorkOrderOp?.workOrderOperationRef ===
      latestActivitySet?.workOrderOperationRef;

    if (workOrderOpMatchesActivitySet && currentWorkOrderOp) {
      return formatOrderOpName(currentWorkOrderOp, jobDisplayNameFormat);
    }

    return _get(latestJob, 'displayName', '');
  }
);

export { getLatestJobName };
